/**
 * @module builder
 */
import type {
  AreaSubstrate,
  OptionsDocument,
  QuoteDocument,
  QuoteProduct,
  TieredPackageProductRule,
  TieredPackages,
  TieredPackageTierId
} from 'paintscout'
import type { ProductSwap } from '../util/swap-quote-products/swap-quote-products'
import { buildLineItemMaterial, buildRawQuoteProduct } from '../util/swap-quote-products/build-quote-product'
import type { UpdateableProduct } from '../area-substrates'
import { getProducts } from '../../options/get-products/get-products'
import type { Product } from '../../options'
import { filterArchived } from '../util/filter-item'

/**
 * The product half of Tiered Packages, with no recalculation in it.
 *
 * Everything here is pure on purpose: `calculateQuote` calls `withSelectedTierProducts` on its way
 * in, so this module must not import the calculator back. `applyTierProducts` (apply-tier-products)
 * is the same swap plus a recalculation, and is what callers outside the calculator use.
 */

/**
 * Resolve which product a rule puts on a given tier.
 *
 * A tier with no entry keeps the base product, which is what makes a partly-filled rule safe: set
 * only an upgrade and the other tiers still price and read correctly.
 */
export function getRuleProductForTier(rule: TieredPackageProductRule, tierId: TieredPackageTierId): string {
  return rule.products?.[tierId] || rule.baseProductKey
}

/**
 * Turn the rule list into the product changes a tier move implies, as from/to pairs.
 *
 * Reports what the rules say, which is all `hasTierProductSwaps` needs. It is not how the swap is
 * applied — see `swapTierProducts`, which resolves each product slot from its own anchor so two
 * rules sharing a target stay distinguishable.
 */
export function getTierProductSwaps(args: {
  productRules?: TieredPackageProductRule[]
  fromTierId: TieredPackageTierId
  toTierId: TieredPackageTierId
}): ProductSwap[] {
  const { productRules, fromTierId, toTierId } = args

  if (!productRules?.length || fromTierId === toTierId) {
    return []
  }

  return productRules.reduce((acc: ProductSwap[], rule) => {
    if (!rule?.baseProductKey) {
      return acc
    }

    const fromProductKey = getRuleProductForTier(rule, fromTierId)
    const toProductKey = getRuleProductForTier(rule, toTierId)

    if (fromProductKey === toProductKey) {
      return acc
    }

    // No target: rules are unscoped, so a change reaches every rate section and every line item.
    acc.push({ fromProductKey, toProductKey })

    return acc
  }, [])
}

/**
 * Whether moving to `tierId` changes any product relative to the base tier.
 *
 * Drives the pricing decision in `tierAdjustment`: a tier that swaps products is priced by the
 * swap, so its percentage/dollar adjustment must not also apply.
 */
export function hasTierProductSwaps(args: { tieredPackages?: TieredPackages; tierId: TieredPackageTierId }): boolean {
  const { tieredPackages, tierId } = args

  if (!tieredPackages?.productRules?.length) {
    return false
  }

  return (
    getTierProductSwaps({
      productRules: tieredPackages.productRules,
      fromTierId: tieredPackages.baseTierId,
      toTierId: tierId
    }).length > 0
  )
}

/**
 * Whether every product named by the rules exists in this options document.
 *
 * The customer view receives a cut-down product library (filterOptionsDetails), so a swap there can
 * resolve nothing and silently no-op. Callers that would write prices back onto the quote must check
 * this first, or a customer-side recalculation would overwrite the estimator's correct numbers with
 * numbers computed from missing products.
 */
export function canResolveTierProducts(args: { tieredPackages?: TieredPackages; options: OptionsDocument }): boolean {
  const { tieredPackages, options } = args

  const rules = tieredPackages?.productRules
  if (!rules?.length) {
    return false
  }

  const values = options?.options?.products?.values ?? {}

  return rules.every((rule) => {
    const keys = [rule?.baseProductKey, ...Object.values(rule?.products ?? {})].filter(Boolean)
    return keys.every((key) => !!values[key])
  })
}

type CoverageSnapshot = QuoteProduct['tierBaseCoverage']

function readCoverage(quoteProduct: QuoteProduct): CoverageSnapshot {
  return {
    coverage_rate_value: quoteProduct.coverage_rate_value,
    lnft_coverage_rate_value: quoteProduct.lnft_coverage_rate_value,
    quantity_coverage_rate_value: quoteProduct.quantity_coverage_rate_value
  }
}

/** Rules keyed by the product they start from. A product is the base of at most one rule. */
function indexRulesByBase(productRules?: TieredPackageProductRule[]): Record<string, TieredPackageProductRule> {
  return (productRules ?? []).reduce((acc: Record<string, TieredPackageProductRule>, rule) => {
    if (rule?.baseProductKey && !acc[rule.baseProductKey]) {
      acc[rule.baseProductKey] = rule
    }
    return acc
  }, {})
}

/**
 * What one slot should hold on the target tier.
 *
 * `anchor` is the product the slot held on the base tier — the estimate as built. Resolving from it
 * rather than from the slot's current product is what lets two rules share a target: each slot
 * remembers where it came from, so the way back is never ambiguous. It also stops rules chaining,
 * because a slot is resolved once and never fed through a second rule.
 */
function resolveSlot(args: {
  currentKey?: string
  anchorKey?: string
  rulesByBase: Record<string, TieredPackageProductRule>
  toTierId: TieredPackageTierId
}): { anchor: string; target: string; changed: boolean } {
  const { currentKey, anchorKey, rulesByBase, toTierId } = args

  const anchor = anchorKey || currentKey || ''
  const rule = anchor ? rulesByBase[anchor] : undefined
  const target = rule ? getRuleProductForTier(rule, toTierId) : anchor

  return { anchor, target, changed: !!target && target !== currentKey }
}

/**
 * Walk every area-substrate product slot on the quote, in a stable order.
 * Archived areas are skipped, matching swapQuoteProducts.
 */
function eachAreaProduct(
  quote: QuoteDocument,
  visit: (quoteProduct: QuoteProduct, replace: (next: QuoteProduct) => void) => void
) {
  Object.keys(quote.areas ?? {}).forEach((areaKey) => {
    const area = quote.areas[areaKey]
    if (filterArchived(area) || !area?.substrates) {
      return
    }

    Object.keys(area.substrates).forEach((section) => {
      ;(area.substrates[section] ?? []).forEach((substrate: AreaSubstrate) => {
        if (!substrate) {
          return
        }

        // Legacy singular product (older quotes/templates) — kept in its singular shape.
        if (substrate.product) {
          visit(substrate.product, (next) => {
            substrate.product = next
          })
        }
        ;(substrate.products ?? []).forEach((quoteProduct, index) => {
          visit(quoteProduct, (next) => {
            substrate.products[index] = next
          })
        })
      })
    })
  })
}

/** Walk every line-item material slot on the quote. Archived line items are skipped. */
function eachLineItemMaterial(
  quote: QuoteDocument,
  visit: (material: UpdateableProduct, replace: (next: UpdateableProduct) => void) => void
) {
  Object.keys(quote.lineItems ?? {}).forEach((lineItemKey) => {
    const lineItem = quote.lineItems[lineItemKey]
    if (filterArchived(lineItem) || !lineItem?.materials) {
      return
    }

    lineItem.materials.forEach((material: UpdateableProduct, index: number) => {
      visit(material, (next) => {
        lineItem.materials[index] = next
      })
    })
  })
}

/**
 * Move one area-substrate slot onto its target product, carrying the base tier's coverage.
 *
 * A rate can override the coverage of its own default product, and that override is baked into the
 * substrate's coverage values when it is created. The rate snapshot stored on the substrate omits
 * `default_products`, so once the slot is rebuilt from the library the override cannot be looked up
 * again — half the rates in the seed data set one, and without this the estimate returns from a tier
 * at a different volume and a different price. So the base coverage rides along on the product and
 * is applied again the moment the slot returns to its anchor.
 */
function applyToAreaProduct(args: {
  quoteProduct: QuoteProduct
  matched: Product
  anchor: string
  isAnchor: boolean
  wasAnchor: boolean
}): QuoteProduct {
  const { quoteProduct, matched, anchor, isAnchor, wasAnchor } = args

  const next: QuoteProduct = {
    ...buildRawQuoteProduct(matched),
    volume: quoteProduct.volume,
    volume_override: quoteProduct.volume_override,
    volume_override_value: quoteProduct.volume_override_value
  }

  if (isAnchor) {
    // Back on the base product: restore whatever coverage it had, then drop both notes.
    const restore = wasAnchor ? readCoverage(quoteProduct) : quoteProduct.tierBaseCoverage
    if (restore) {
      Object.assign(next, restore)
    }
    delete next.tierBaseCoverage
    delete next.tierBaseProductKey
    return next
  }

  // Leaving the base product, or moving between two non-base tiers — keep both notes alive.
  const carried = wasAnchor ? readCoverage(quoteProduct) : quoteProduct.tierBaseCoverage
  if (carried) {
    next.tierBaseCoverage = carried
  }
  next.tierBaseProductKey = anchor

  return next
}

/**
 * Whether any slot on the quote sits on a product other than the one `toTierId` calls for.
 *
 * Reads the quote without copying it, so the common case — every slot already on the right product
 * — costs one walk instead of a deep clone. It ignores the product library, so it can answer true
 * for a target the library cannot resolve; `swapTierProducts` then finds nothing to change and
 * returns null, which is the same answer one step later.
 */
function hasSlotsOffTier(args: {
  quote: QuoteDocument
  rulesByBase: Record<string, TieredPackageProductRule>
  toTierId: TieredPackageTierId
}): boolean {
  const { quote, rulesByBase, toTierId } = args
  let found = false

  eachAreaProduct(quote, (quoteProduct) => {
    if (found) {
      return
    }
    found = resolveSlot({
      currentKey: quoteProduct?.product?.name,
      anchorKey: quoteProduct?.tierBaseProductKey,
      rulesByBase,
      toTierId
    }).changed
  })

  if (found) {
    return true
  }

  eachLineItemMaterial(quote, (material) => {
    if (found) {
      return
    }
    found = resolveSlot({
      currentKey: material?.key,
      anchorKey: material?.tierBaseProductKey,
      rulesByBase,
      toTierId
    }).changed
  })

  return found
}

/**
 * Put every product slot on the quote onto the product `toTierId` calls for.
 *
 * Each slot is resolved from the product it held on the base tier, not from the product it holds
 * now, so the move works from any tier to any other and a round trip lands exactly where it
 * started. That also makes it idempotent, which is what lets `calculateQuote` run it on every pass.
 *
 * Returns null when nothing moved, so the caller can skip a recalculation it does not need.
 */
export function swapTierProducts(args: {
  quote: QuoteDocument
  options: OptionsDocument
  toTierId: TieredPackageTierId
}): QuoteDocument | null {
  const { options, toTierId } = args

  const productRules = args.quote.tieredPackages?.productRules
  if (!productRules?.length) {
    return null
  }

  const rulesByBase = indexRulesByBase(productRules)
  if (!hasSlotsOffTier({ quote: args.quote, rulesByBase, toTierId })) {
    return null
  }

  const library = getProducts({ options })
  const quote = JSON.parse(JSON.stringify(args.quote)) as QuoteDocument
  let changedAny = false

  eachAreaProduct(quote, (quoteProduct, replace) => {
    const currentKey = quoteProduct?.product?.name
    const { anchor, target, changed } = resolveSlot({
      currentKey,
      anchorKey: quoteProduct?.tierBaseProductKey,
      rulesByBase,
      toTierId
    })

    if (!changed) {
      return
    }

    const matched = library.find((product) => product.key === target)
    if (!matched) {
      // The customer view carries a cut-down library; leaving the slot alone beats blanking it.
      return
    }

    changedAny = true
    replace(
      applyToAreaProduct({
        quoteProduct,
        matched,
        anchor,
        isAnchor: target === anchor,
        wasAnchor: currentKey === anchor
      })
    )
  })

  eachLineItemMaterial(quote, (material, replace) => {
    const { anchor, target, changed } = resolveSlot({
      currentKey: material?.key,
      anchorKey: material?.tierBaseProductKey,
      rulesByBase,
      toTierId
    })

    if (!changed) {
      return
    }

    const matched = library.find((product) => product.key === target)
    if (!matched) {
      return
    }

    changedAny = true
    const next = buildLineItemMaterial(matched, material)

    // Line-item materials carry no rate coverage override, so only the anchor needs carrying.
    if (target === anchor) {
      delete next.tierBaseProductKey
    } else {
      next.tierBaseProductKey = anchor
    }

    replace(next)
  })

  return changedAny ? quote : null
}

/**
 * Put the quote's products back in step with the tier it is sitting on.
 *
 * `calculateQuote` calls this before it calculates, which is what keeps one invariant true: on a
 * non-base tier with product rules, the substrates on the quote are that tier's substrates, so
 * `totals.price` IS that tier's price. Both `tierAdjustment` and `getTieredPackageBasePrice` rely
 * on it, and until this ran on every calculation it held only immediately after a tier switch.
 *
 * Anything that adds a product slot — a new area, a new substrate, a line item with materials, a
 * copied area — creates it holding the base product, because none of those paths knows about tiers.
 * Before this, such a slot kept the base product until the estimator switched packages again: the
 * estimate then read as Best while it was priced and built as the base tier (ENG-1825).
 *
 * A no-op in the customer view, where the cut-down product library leaves the swap unresolvable.
 */
export function withSelectedTierProducts(args: { quote: QuoteDocument; options: OptionsDocument }): QuoteDocument {
  const { quote, options } = args
  const tieredPackages = quote?.tieredPackages

  if (
    !tieredPackages?.enabled ||
    !tieredPackages.tiers ||
    !tieredPackages.selectedTierId ||
    tieredPackages.selectedTierId === tieredPackages.baseTierId ||
    !canResolveTierProducts({ tieredPackages, options })
  ) {
    return quote
  }

  return swapTierProducts({ quote, options, toTierId: tieredPackages.selectedTierId }) ?? quote
}
